import type { ForwardRefRenderFunction, SyntheticEvent } from "react";
import { forwardRef, useImperativeHandle, useRef } from "react";

import type { IAutocompleteProps } from "@similarweb/ui-components";
import { DotsLoader, ListItemSeparator } from "@similarweb/ui-components";
import { Stack } from "@similarweb/ui-components/foundations";

import { autocompleteStates } from "app/components/compare/WebsiteQueryBarTypes";
import { translate } from "app/filters/i18nFilter";
import { getWebsiteResults } from "app/services/solutions2Services/HomepageDataFetchers/NewModulesHomepageDataFetcher";

import { ScrollAreaWrap } from "./components/ScrollAreaWrapper";
import {
    AutocompleteStyled,
    CustomIndicator,
    ListItemWebsitePartialSelectionStyled,
    ListItemSeparatorStyled,
} from "./styles";
import type {
    IAutocompleteWebsitesCompareItemProps,
    IAutocompleteWebsitesCompareItemWrap,
    IAutocompleteWebsiteShape,
    ICompareItem,
    IWebsitePageResult,
} from "./types";
import { getUrlComponents } from "./urlComponentUtils";
import {
    getPageLevelAutocompleteRecents,
    getCustomIndicator,
    getListItemsGroupsContent,
    getPageLevelAutoCompleteItems,
    renderItemCreator,
    handleGetListItems,
    filterOutExcludes,
    filterOutBlockList,
    filterOutQuery,
} from "./utils";

const normalizedSimilarSite = (item: FixmeType) => {
    const { subdomain } = getUrlComponents(item.Domain);
    const secondaryText = getCustomIndicator()(item.Domain);
    return {
        name: item.Domain,
        icon: item.Favicon,
        searchType: subdomain ? "subdomain" : "domain",
        secondaryText,
    };
};

const normalizedSearches = (item: IAutocompleteWebsiteShape) => {
    return {
        name: item.name,
        icon: item.image,
    };
};

const AutocompleteWebsitesCompareItemRenderFunction: ForwardRefRenderFunction<
    IAutocompleteWebsitesCompareItemWrap,
    IAutocompleteWebsitesCompareItemProps
> = (props, ref) => {
    const {
        onClick,
        defaultValue,
        defaultSearchType,
        similarSites,
        recentSearches,
        showCompareSites,
        excludes,
        autocompleteProps,
        onCloseButtonClick,
        className,
        onKeyUp,
        onArrowKeyPress,
        overrideEnterFunc,
        overrideGetWebsites = getWebsiteResults,
        AutocompleteWidth = 500,
        customItemStructure,
        modifySearchResult,
        renderSimilarSitesHead,
        renderSearchResultsHead,
        renderNoDataSimilarsitesComponent,
        renderNoDataSearchesComponent,
        autoCompleteDropdownRef,
        showExtendedTypeResults,
    } = props;
    const autocompleteRef = useRef<FixmeType>(null);

    useImperativeHandle(ref, () => ({
        truncateResults: (force: boolean) => {
            autocompleteRef.current?.truncateResults(force);
        },
    }));

    const onItemClick = (item: IWebsitePageResult, type: string) => () => {
        onClick(item, type);
    };

    const onEnter = (event: SyntheticEvent, item?: ICompareItem) => overrideEnterFunc?.(event, item);

    const getListItems = async (query: string) => {
        let pageLevelItems: FixmeType[] = [];
        const { domain, subdomain, folder, queryParams } = getUrlComponents(query);

        const getRecents = () => {
            return getPageLevelAutocompleteRecents(
                recentSearches,
                excludes ?? [],
                showCompareSites ?? false,
                onItemClick,
                true,
            );
        };

        const getSimilarSites = () => {
            return similarSites
                .filter((x: FixmeType) => !(excludes ?? []).find((exclude) => exclude.url === (x.Domain ?? x.name)))
                .reduce((results: FixmeType[], item: FixmeType) => {
                    const normalizedItem = customItemStructure
                        ? customItemStructure(item)
                        : normalizedSimilarSite(item);
                    results.push(renderItemCreator("similar", onItemClick)(normalizedItem));
                    return results;
                }, []);
        };

        if (domain) {
            pageLevelItems = await getPageLevelAutoCompleteItems(
                onItemClick,
                query,
                domain,
                subdomain,
                folder,
                queryParams,
                query === defaultValue ? getSimilarSites() : [],
                excludes,
                undefined,
                showExtendedTypeResults,
            );
            if (folder?.length > 1 || queryParams?.length) {
                return pageLevelItems;
            }
        }
        if (query?.length) {
            try {
                const queries = await overrideGetWebsites(query);
                if (queries.length === 0) {
                    return [autocompleteStates.NO_DATA_SEARCHES];
                }
                const modifiedSearchResult = modifySearchResult ? modifySearchResult(queries) : queries;

                let results = modifiedSearchResult
                    .filter(filterOutExcludes(excludes ?? []))
                    .filter(filterOutBlockList)
                    .map((item: FixmeType) => {
                        const normalizedItem = customItemStructure
                            ? customItemStructure(item)
                            : normalizedSearches(item);
                        const { subdomain: normalizedItemSubdomain } = getUrlComponents(normalizedItem.name);
                        return renderItemCreator(
                            "query",
                            onItemClick,
                        )({
                            ...(normalizedItem as IAutocompleteWebsiteShape),
                            searchType: normalizedItemSubdomain ? "subdomain" : "domain",
                            secondaryText: normalizedItem.secondaryText ?? getCustomIndicator()(normalizedItem.name),
                        });
                    });
                if (pageLevelItems.length) {
                    results = results.filter(filterOutQuery(query));
                }
                return [...pageLevelItems, ...results];
            } catch (e) {
                return [autocompleteStates.ERROR];
            }
        }

        if (similarSites[0] === autocompleteStates.NO_DATA_SIMILARSITES) {
            return similarSites;
        }

        return [...getRecents().recents.slice(0, 3), ...getSimilarSites()];
    };

    const getSimilarSitesHead = () => {
        if (typeof renderSimilarSitesHead === "function") {
            return renderSimilarSitesHead();
        }

        return (
            <ListItemSeparatorStyled>
                {translate("autocomplete.websitepagesmain.seperator.text.similar")}
            </ListItemSeparatorStyled>
        );
    };

    const getSearchResultsHead = () => {
        if (typeof renderSearchResultsHead === "function") {
            return renderSearchResultsHead();
        }

        return null;
    };

    const renderItems = ({
        selectedItemId,
        listItems,
    }: // @ts-ignore FIXME: strict migration - IAutocompleteProps["renderItems"] may be undefined — extracting Parameters from optional function
    Parameters<IAutocompleteProps["renderItems"]>[0] & {
        listItems: Array<ICompareItem | autocompleteStates>;
    }) => {
        if (!listItems.length) {
            return null;
        }

        const itemCreator = (item: ICompareItem, index: number) => {
            const { name, icon, image } = item;
            const active = index === selectedItemId;
            return (
                <ListItemWebsitePartialSelectionStyled
                    key={`${item.name}_${index}`}
                    isActive={active}
                    text={name}
                    secondaryText={item.secondaryText}
                    onClick={() => item.props.onClick(item)}
                    selectedTextIndexes={item.selectedTextIndexes}
                    content={item.afterElementText}
                    reverseEllipsis={item.reverseEllipsis}
                    img={icon ?? image}
                />
            );
        };

        const { recents, similar, searchResults } = getListItemsGroupsContent({
            listItems,
            renderListItem: itemCreator,
            renderNoSearchData: renderNoDataSearchesComponent,
            renderNoSimilarsitesData: renderNoDataSimilarsitesComponent,
        });

        return (
            <div
                className="ListItemsContainer"
                ref={autoCompleteDropdownRef}
            >
                <Stack gap="8px">
                    {searchResults.length > 0 ? (
                        <section>
                            {getSearchResultsHead()}
                            <ScrollAreaWrap>{searchResults}</ScrollAreaWrap>
                        </section>
                    ) : null}
                    {recents.length > 0 ? (
                        <section>
                            <ListItemSeparator>
                                {translate("autocomplete.websitePagesMain.seperator.text.recents")}
                            </ListItemSeparator>
                            <ScrollAreaWrap>{recents}</ScrollAreaWrap>
                        </section>
                    ) : null}
                    {similar.length > 0 ? (
                        <section>
                            {getSimilarSitesHead()}
                            <ScrollAreaWrap>{similar}</ScrollAreaWrap>
                        </section>
                    ) : null}
                </Stack>
            </div>
        );
    };

    const getAutocompleteIndicator = (query: string) => {
        const customIndicator = getCustomIndicator(defaultSearchType, defaultValue)(query);
        return <CustomIndicator>{customIndicator}</CustomIndicator>;
    };

    return (
        <AutocompleteStyled
            className={className}
            isFocused
            width={AutocompleteWidth}
            selectedValue={defaultValue}
            autoFocus
            getListItems={handleGetListItems(defaultSearchType ?? "domain", defaultValue ?? "", getListItems)}
            renderItems={renderItems}
            loadingComponent={<DotsLoader />}
            preventTruncateUnlessForced
            onCloseClick={onCloseButtonClick}
            resetValueOnSelect
            floating
            debounce={250}
            maxResults={20}
            onKeyUp={onKeyUp}
            onArrowKeyPress={onArrowKeyPress}
            overrideEnterFunc={onEnter}
            initialSelectedItemId={-1}
            ref={autocompleteRef}
            getCustomIndicator={getAutocompleteIndicator}
            {...autocompleteProps}
        />
    );
};
export const AutocompleteWebsitePagesCompareItem = forwardRef(AutocompleteWebsitesCompareItemRenderFunction);
